/**
 * De stand van de maandruimte in het aanvraagformulier (Story 6.2, FR-5).
 *
 * DEZE HOOK MAG HET FORMULIER NOOIT OPHOUDEN (KE-4, AC-8). Hij is informatief;
 * of er een HR-stap nodig is stelt de server bij het indienen hoe dan ook zelf
 * vast (KE-5). Alles wat misgaat — een netwerk dat wegvalt, een serverfout,
 * een verzoek dat blijft hangen — komt daarom als ÉÉN toestand terug:
 * *niet op te halen*. De indienknop blijft in al die gevallen aan.
 *
 * DAAROM STAAT ER EEN EIGEN TIJDGRENS OMHEEN. Zonder die grens blijft een
 * verzoek dat nooit antwoordt eeuwig "aan het laden", en dan verschijnt er ook
 * nooit een zin die vertelt dat het getal er niet is — de medewerker staart
 * naar een lege plek in plaats van naar een antwoord. `retry: false` hoort
 * daarbij: opnieuw proberen rekt precies de stilte op die deze grens wegneemt.
 *
 * ANDERHALVE SECONDE, EN GEEN HALVE (code review 2026-08-17). De grens hoort
 * te vangen wat wegblijft, niet wat traag is. Een halve seconde is op een
 * gewone verbinding al krap voor twee query's plus de heen- en terugweg; wie
 * daar net overheen gaat kreeg het getal dat er wél was niet te zien, en dat
 * is precies de lege plek die deze grens moest wegnemen. Anderhalve seconde
 * laat een normaal antwoord door en houdt het wachten kort genoeg om niet als
 * blokkade te voelen — de indienknop blijft ondertussen hoe dan ook aan.
 */

import { useQuery } from '@tanstack/react-query';
import type { MaandruimteStand } from '@functieloonhuis/shared';
import { verlofApi } from '../../../lib/verlofApi';

/** Hoe lang het formulier op dit getal wacht voordat het zonder verder gaat. */
export const MAANDRUIMTE_WACHTTIJD_MS = 1500;

export const maandruimteKeys = {
  voor: (datum: string, uren: number | null, employeeId: number | undefined) =>
    ['leave-requests', 'maandruimte', datum, uren, employeeId ?? null] as const,
};

interface MaandruimteOpties {
  /** De startdatum van de aanvraag (`JJJJ-MM-DD`); leeg zolang er geen is. */
  datum: string;
  /** De uren van de aanvraag in wording, of `null` zolang die onbekend zijn. */
  uren: number | null;
  /** Alleen bij een namens-aanvraag. */
  employeeId?: number;
  /**
   * Alleen vragen wanneer er iets te vragen valt: de soort telt mee in de
   * maandruimte én er is een datum. Bij elke andere soort en elke andere
   * verlofgroep gaat er GEEN verzoek uit (AC-10).
   */
  enabled: boolean;
}

/**
 * Een belofte die opgeeft wanneer hij te lang duurt.
 *
 * Bewust een `race` en geen `AbortController`: een verzoek afbreken werkt
 * alleen als de onderliggende laag het signaal ook honoreert, en wat deze hook
 * nodig heeft is niet dat de verbinding sluit maar dat er een ANTWOORD komt —
 * al is het "we weten het niet".
 */
function metTijdgrens<T>(belofte: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('maandruimte: geen antwoord binnen de tijdgrens')),
      ms
    );
    belofte.then(
      (waarde) => {
        clearTimeout(timer);
        resolve(waarde);
      },
      (fout) => {
        clearTimeout(timer);
        reject(fout);
      }
    );
  });
}

export function useMaandruimte({ datum, uren, employeeId, enabled }: MaandruimteOpties) {
  return useQuery<MaandruimteStand | null>({
    queryKey: maandruimteKeys.voor(datum, uren, employeeId),
    queryFn: async () => {
      const antwoord = await metTijdgrens(
        verlofApi.getMaandruimte(datum, uren ?? undefined, employeeId),
        MAANDRUIMTE_WACHTTIJD_MS
      );
      return antwoord.data;
    },
    enabled: enabled && datum.length > 0,
    retry: false,
    staleTime: 1000 * 30,
  });
}
